/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { ReactElement } from "react";
import ScaleLoader from "react-spinners/ScaleLoader";
import styled, { ThemeProvider } from "styled-components";

import { redesignTheme } from "../theme/theme";

const checkCircle = require("../svg/checkCircle.svg");

type CardProps = {
  bodyPadding?: string;
  buttons?: ReactElement | ReactElement[];
  children: ReactElement | ReactElement[];
  loading?: boolean;
  subtitle?: string | ReactElement;
  successMessage?: string;
  testId?: string;
  title?: string;
};

export const StyledCardWrapper = styled.div`
  background: ${redesignTheme.color.white};
  border-radius: ${redesignTheme.radius.lg};
  box-shadow: none;
  font-family: ${redesignTheme.fontFamily.interface};
  position: relative;
`;

const StyledCardTitleArea = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTitle = styled.h2`
  padding-top: ${redesignTheme.spacing[6]};
  padding-left: ${redesignTheme.spacing[6]};
  margin-bottom: 0;
  font-size: ${redesignTheme.fontSize[300]};
  line-height: 32px;
  font-weight: ${redesignTheme.fontWeight.semibold};
  font-family: ${redesignTheme.fontFamily.interface};
  width: 300px;
  color: ${redesignTheme.color.gray900};
`;

export const StyledSuccessMessage = styled.div`
  color: ${redesignTheme.color.gray900};
  font-family: ${redesignTheme.fontFamily.interface};
  background: ${redesignTheme.color.green50};
  padding: ${redesignTheme.spacing[2]} ${redesignTheme.spacing[2.5]};
  border-radius: ${redesignTheme.radius.sm};
  font-size: ${redesignTheme.fontSize[50]};
  margin: ${redesignTheme.spacing[4.5]} ${redesignTheme.spacing[6]} 0
    ${redesignTheme.spacing[6]};
  display: flex;
  vertical-align: middle;
`;

const StyledImg = styled.img`
  height: 19px;
  width: 19px;
  margin-right: ${redesignTheme.spacing[2.5]};
`;

const StyledSubtitle = styled.h3`
  padding-top: ${redesignTheme.spacing[4.5]};
  padding-left: ${redesignTheme.spacing[6]};
  margin-bottom: 0;
  font-size: ${redesignTheme.fontSize[200]};
  line-height: 28px;
  font-weight: ${redesignTheme.fontWeight.semibold};
  font-family: ${redesignTheme.fontFamily.interface};
  text-transform: none;
  letter-spacing: normal;
  color: ${redesignTheme.color.gray600};
`;

const StyledLoaderContainer = styled.div`
  text-align: center;
  padding: ${redesignTheme.spacing[10]} 0;
`;

const StyledBody = styled.div`
  padding: ${(props): string => {
    return props.theme.bodyPadding;
  }};
`;

const Card = ({
  bodyPadding,
  buttons,
  children,
  loading,
  subtitle,
  successMessage,
  testId,
  title,
}: CardProps): ReactElement => {
  const theme = {
    bodyPadding,
  };

  return (
    <ThemeProvider theme={theme}>
      <StyledCardWrapper>
        {title && (
          <StyledCardTitleArea>
            <StyledTitle>{title}</StyledTitle>
            {buttons}
          </StyledCardTitleArea>
        )}
        {successMessage && (
          <StyledSuccessMessage>
            <StyledImg src={checkCircle} alt="success check" />
            {successMessage}
          </StyledSuccessMessage>
        )}
        {loading ? (
          <StyledLoaderContainer data-testid="loader">
            <ScaleLoader
              color={redesignTheme.color.gray900}
              loading={loading}
              height={30}
              width={4}
              margin={4}
            />
          </StyledLoaderContainer>
        ) : (
          <>
            {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
            <StyledBody data-testid={testId}>{children}</StyledBody>
          </>
        )}
      </StyledCardWrapper>
    </ThemeProvider>
  );
};

Card.defaultProps = {
  bodyPadding: "16px 24px 24px 24px",
  loading: false,
  subtitle: undefined,
  testId: "",
};

export default Card;
